import { useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { shallow } from 'zustand/shallow';
import { useFiltersStore } from '../stores';
import {
  FILTER_SECTION_KEYS_FAIR,
  FILTER_SECTION_KEYS_NON_FAIR,
  countForFilterSection,
} from '../util/filter-counts';
import FilterStats from './FilterStats';

const FILTER_LABELS = {
  TYPE: 'Type',
  MEDIUM: 'Medium',
  'ON VIEW': 'On view',
  NEIGHBORHOOD: 'Neighborhood',
  SUBJECT: 'Subject',
  STYLE: 'Style',
};

/**
 * Full filter sheet (aligned with showrunner-mobile FiltersPane + FilterTagsList).
 */
const FeedFiltersPane = ({
  isOpen,
  onClose,
  isFetching,
  isFair,
  filterMap,
  shows,
  recommendedShows,
  onResetFilters,
}) => {
  const {
    open,
    selectedMediums,
    selectedLocation,
    selectedSubject,
    selectedStyles,
    showType,
    setAllArtMedium,
    addToFilter,
    removeFromFilter,
    ...counts
  } = useFiltersStore(
    s => ({
      open: s.open,
      selectedMediums: s.selectedMediums,
      selectedLocation: s.selectedLocation,
      selectedSubject: s.selectedSubject,
      selectedStyles: s.selectedStyles,
      showType: s.showType,
      setAllArtMedium: s.setAllArtMedium,
      addToFilter: s.addToFilter,
      removeFromFilter: s.removeFromFilter,
      openCount: s.open.size,
      selectedMediumsCount: s.selectedMediums.size,
      selectedStylesCount: s.selectedStyles.size,
      selectedSubjectCount: s.selectedSubject.size,
      selectedLocationCount: s.selectedLocation.size,
      showTypeCount: s.showType.size,
    }),
    shallow
  );

  const FILTER_SECTIONS = isFair ? FILTER_SECTION_KEYS_FAIR : FILTER_SECTION_KEYS_NON_FAIR;

  const FILTERS_HASH = {
    MEDIUM: { key: 'selectedMediums', filter: selectedMediums },
    'ON VIEW': { key: 'open', filter: open },
    NEIGHBORHOOD: { key: 'selectedLocation', filter: selectedLocation },
    SUBJECT: { key: 'selectedSubject', filter: selectedSubject },
    STYLE: { key: 'selectedStyles', filter: selectedStyles },
    TYPE: { key: 'showType', filter: showType },
  };

  const handleSelect = (currentKey, option) => {
    const { key, filter } = FILTERS_HASH[currentKey];

    if (option === 'All Art') {
      setAllArtMedium();
      return;
    }

    if (filter.has(option)) {
      removeFromFilter(key, option);
    } else {
      addToFilter(key, option);
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close filters"
        onClick={onClose}
      />
      <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl font-oswald">
        <div className="flex shrink-0 items-center justify-end border-b border-slate-100 px-3 py-3">
          <button
            type="button"
            className="rounded-full p-2 text-gray-700 hover:bg-gray-100"
            onClick={onClose}
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col divide-y divide-slate-100 px-4 py-2">
            {FILTER_SECTIONS.map(sectionKey => {
              const sectionCount = countForFilterSection(counts, sectionKey);
              return (
                <div key={sectionKey} className="flex flex-col gap-4 py-4 first:pt-2">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-black">{FILTER_LABELS[sectionKey]}</h2>
                    {sectionCount > 0 && (
                      <span
                        className="inline-flex h-7 min-w-[1.75rem] shrink-0 items-center justify-center rounded-full bg-neutral-500 px-1.5 text-xs font-semibold !text-white tabular-nums shadow-sm"
                        aria-label={`${sectionCount} selected in this category`}
                      >
                        {sectionCount}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap justify-center gap-1.5">
                    {filterMap[sectionKey]?.options?.map(option => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => handleSelect(sectionKey, option)}
                        className={`rounded-lg border px-2 py-1.5 text-sm ${
                          filterMap[sectionKey].selected.has(option)
                            ? 'border-secondary-red bg-secondary-red text-white'
                            : 'border-slate-200 bg-white text-black/70'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {isFetching && (
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center bg-gray-900/25"
            aria-busy="true"
            aria-label="Loading results"
          >
            <span className="loading loading-spinner loading-lg text-secondary-red" />
          </div>
        )}

        <div className="shrink-0 border-t border-slate-100 bg-white px-2 py-3">
          <FilterStats shows={shows} recommendedShows={recommendedShows} resetFilters={onResetFilters} />
        </div>
      </div>
    </div>
  );
};

export default FeedFiltersPane;
